export type QueuedHook = {
    intervals: number[];
    rhythm: number[]; // Rhythmic durations in beats (quantized)
    peakNoteValue?: number;  // P3 FIX: Track peak note for better callback timing
};

/**
 * V9 MELODIC MEMORY: Transformation strategies for theme callbacks
 */
export type ThemeTransform = 'original' | 'retrograde' | 'inversion' | 'transposition';

/**
 * ThemeEngine
 * P3 FIX: Enhanced with obligatory hook callbacks and peak note tracking
 */
export class ThemeEngine {
    // Stores the "Canonical Hook" for each section
    private hooks: Map<string, QueuedHook> = new Map();
    private isRecording: boolean = false;
    private currentBuffer: number[] = []; // Steps/Intervals
    private currentRhythmBuffer: number[] = []; // Durations in beats

    // V9 MELODIC MEMORY: Seed phrase storage
    private seedPhrase: number[] = [];
    private hasSeedPhrase: boolean = false;
    private seedCaptureBuffer: number[] = [];
    private callbackTriggeredSections: Set<string> = new Set();

    // P3 FIX: Track peak notes for better seed capture
    private currentPeakNoteValue: number = 0;
    private seedPhrasePeakValue: number = 0;

    // P3 FIX: Track if we're in final chorus (obligatory hook)
    private isInFinalChorus: boolean = false;

    constructor() {
        this.reset();
    }

    reset() {
        this.hooks.clear();
        this.currentBuffer = [];
        this.currentRhythmBuffer = [];
        this.isRecording = false;
        // V9: Reset seed phrase
        this.seedPhrase = [];
        this.hasSeedPhrase = false;
        this.seedCaptureBuffer = [];
        this.callbackTriggeredSections.clear();
        // P3 FIX: Reset peak tracking
        this.currentPeakNoteValue = 0;
        this.seedPhrasePeakValue = 0;
        this.isInFinalChorus = false;
    }

    /**
     * P3 FIX: Set whether we're in the final chorus (for obligatory hooks)
     */
    public setInFinalChorus(inFinalChorus: boolean): void {
        this.isInFinalChorus = inFinalChorus;
    }

    /**
     * P3 FIX: Track peak notes for better seed phrase capture
     */
    public trackNoteValue(midiNote: number): void {
        if (midiNote > this.currentPeakNoteValue) {
            this.currentPeakNoteValue = midiNote;
        }
    }

    /**
     * Determines if we should capture the current melody as a "Hook".
     * Only captures if we don't already have one for this section.
     */
    public shouldCapture(section: string): boolean {
        return !this.hooks.has(section);
    }

    public recordStep(interval: number, durationBeats?: number) {
        // Simple buffer for now, could be expanded to capture rhythm
        this.currentBuffer.push(interval);

        // Record rhythm if provided
        if (durationBeats !== undefined) {
            this.currentRhythmBuffer.push(durationBeats);
        } else {
            // Fallback: assume quarter note if missing
            this.currentRhythmBuffer.push(1.0);
        }

        // V9 MELODIC MEMORY: Also record to seed capture buffer if in Verse
        if (!this.hasSeedPhrase) {
            this.seedCaptureBuffer.push(interval);

            // Capture seed when we have 6+ notes (a substantial phrase)
            if (this.seedCaptureBuffer.length >= 6) {
                this.seedPhrase = [...this.seedCaptureBuffer];
                this.hasSeedPhrase = true;
                if (process.env.NODE_ENV === 'development') console.log(`[ThemeEngine] Seed Phrase captured: [${this.seedPhrase.join(', ')}]`);
            }
        }
    }

    /**
     * Finalizes the recording of a hook for a section.
     * Called when a phrase ends or section changes.
     */
    public commitHook(section: string) {
        if (this.currentBuffer.length >= 4) { // Only save substantial hooks
            // P3 FIX: Include peak note value in hook
            this.hooks.set(section, {
                intervals: [...this.currentBuffer],
                rhythm: [...this.currentRhythmBuffer], // Capture recorded rhythm
                peakNoteValue: this.currentPeakNoteValue
            });
        }
        this.currentBuffer = [];
        this.currentRhythmBuffer = [];
        this.currentPeakNoteValue = 0;  // P3 FIX: Reset peak for next phrase
    }

    public getHook(section: string): QueuedHook | undefined {
        return this.hooks.get(section);
    }

    public hasHook(section: string): boolean {
        return this.hooks.has(section);
    }

    /**
     * Clears a hook to allow a new one to be generated.
     * Useful for "Bridge" which might want to be different every time.
     */
    public clearHook(section: string) {
        this.hooks.delete(section);
    }

    /**
     * P3 FIX: Get hook for a specific section with optional key transposition
     * Returns the hook intervals transposed to the current key offset
     */
    public getHookForSection(section: string, keyOffset: number = 0): QueuedHook | null {
        const hook = this.hooks.get(section);
        if (!hook) return null;

        // If no transposition needed, return original
        if (keyOffset === 0) return hook;

        // P3 FIX: Transpose intervals (scale the steps by key offset direction)
        // Note: This is a melodic transposition, not a key change
        // The intervals stay the same, but we could adjust the starting point
        return {
            intervals: [...hook.intervals],
            rhythm: [...hook.rhythm],
            peakNoteValue: hook.peakNoteValue ? hook.peakNoteValue + keyOffset : undefined
        };
    }

    // ============================
    // V9 MELODIC MEMORY METHODS
    // ============================

    /**
     * V9: Get a transformed theme callback for Chorus/Bridge sections.
     * Returns intervals to replay, or null if no callback should occur.
     * 
     * P3 FIX: OBLIGATORY in final Chorus (100% chance)
     * Regular Chorus/Bridge: 30% chance per section (only triggers once per section).
     */
    public getThemeCallback(currentSection: string): number[] | null {
        // Only trigger callbacks in Chorus or Bridge
        if (currentSection === 'Verse') return null;

        // Don't callback if no seed phrase captured
        if (!this.hasSeedPhrase || this.seedPhrase.length === 0) return null;

        // Only trigger once per section visit
        if (this.callbackTriggeredSections.has(currentSection)) return null;

        // P3 FIX: OBLIGATORY hook in final Chorus (100% chance)
        const probability = (this.isInFinalChorus && currentSection === 'Chorus') ? 1.0 : 0.3;

        if (Math.random() > probability) {
            this.callbackTriggeredSections.add(currentSection); // Mark as "considered"
            return null;
        }

        this.callbackTriggeredSections.add(currentSection);

        // Choose transformation based on section
        const transform = this.getTransformForSection(currentSection);
        const transformed = this.applyTransform(this.seedPhrase, transform);

        if (process.env.NODE_ENV === 'development') {
            const obligatoryStr = this.isInFinalChorus ? ' (OBLIGATORY)' : '';
            console.log(`[ThemeEngine] Theme Callback: ${transform} in ${currentSection}${obligatoryStr}`);
        }
        return transformed;
    }

    /**
     * V9: Select appropriate transformation for the section.
     */
    private getTransformForSection(section: string): ThemeTransform {
        switch (section) {
            case 'Chorus':
                // Chorus: Transposition (higher energy) or original
                return Math.random() < 0.6 ? 'transposition' : 'original';
            case 'Bridge':
                // Bridge: Inversion or retrograde (more dramatic)
                return Math.random() < 0.5 ? 'inversion' : 'retrograde';
            default:
                return 'original';
        }
    }

    /**
     * V9: Apply transformation to seed phrase intervals.
     */
    private applyTransform(intervals: number[], transform: ThemeTransform): number[] {
        switch (transform) {
            case 'retrograde':
                // Play backwards
                return [...intervals].reverse();

            case 'inversion':
                // Flip direction of all intervals
                return intervals.map(i => -i);

            case 'transposition':
                // Shift all intervals up by 2 scale steps (lift the phrase)
                return intervals.map(i => i + (Math.sign(i) || 1) * 1);

            case 'original':
            default:
                return [...intervals];
        }
    }

    /**
     * V9: Reset callback tracking on section change (allows repeat callbacks on loop).
     */
    public onSectionChange(newSection: string) {
        // When entering a new section, we could optionally clear the triggered set
        // to allow callbacks on subsequent visits. For now, keep it per-session.
        // Uncomment below to allow repeat callbacks:
        // this.callbackTriggeredSections.delete(newSection);
    }
}
